import config from "@/libs/config";
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  defaultDataIdFromObject,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { moveToErrorPage } from "@/libs/errors";
import { getSession } from "@/libs/cognito";
import { getSessionUser } from "@/contexts/AuthContext";
import { LoginSource, useIsLoginFromAegis } from "@/libs/loginSource";

const httpLink = createHttpLink({
  uri: "/graphql",
  credentials: "include",
});

const billingHttpLink = createHttpLink({
  uri: config.BILLING_URL + "/graphql",
});

// Operations that may be called before a session is confirmed (Aegis session init only).
// MiddlewareGraphQL resolves these from the id_token cookie when no Authorization header is present.

const authLink = setContext(async (req, { headers }) => {
  if (publicUserOperations.includes(req.operationName)) {
    return {
      headers: {
        ...headers,
      },
    };
  }

  if (config.USING_AEGIS && localStorage.getItem("aegis_id_token")) {
    const token = localStorage.getItem("aegis_id_token");
    return {
      headers: {
        ...headers,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    };
  }

  // // 別タブなどでサインアウトされていた場合はエラーさせる
  // if (!getSessionUser()) {
  //   // Allow the me query through during Aegis session detection;
  //   // MiddlewareGraphQL will use the id_token cookie if present.
  //   if (aegisSessionCheckOperations.includes(req.operationName)) {
  //     return { headers };
  //   }
  //   moveToErrorPage();
  //   return;
  // }
  const session: any = await getSession();
  if (!session) {
    moveToErrorPage();
    return;
  }

  const token: string = await session.idToken.getJwtToken();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const publicUserOperations: string[] = [
  "registerProvisionalCompanyUser",
  "registerProvisionalCompanyWithBizsol",
  "registerProvisionalCompanyWithConnectBoard",
  "sendForgotPasswordMail",
  "verifyForgotPasswordToken",
  "verifyProvisionalRegistrationToken",
  "verifyProvisionalRegistrationTokenWithBizsol",
  "verifyProvisionalRegistrationTokenWithConnectBoard",
  "registerNewCompany",
  "registerVerifiedCompanyWithBizsol",
  "registerVerifiedCompanyWithConnectBoard",
  "updatePasswordWithToken",
  "signInWithBizsol",
  "registerNewCompanyWithBizsol",
  "registerMemberWithBizsol",
  "servicesForPublic",
  "getBizsolEmail",
  "signInWithConnectBoard",
  "registerNewCompanyWithConnectBoard",
  "registerMemberWithConnectBoard",
  "bankServices",
  "resolveIdp",
];

export const createApolloClient = () => {
  return new ApolloClient({
    link: ApolloLink.split(
      (operation) => operation.getContext().api === "billing",
      authLink.concat(billingHttpLink),
      authLink.concat(httpLink)
    ),
    cache: new InMemoryCache({
      // Disable normalization for Service objects.
      // This is necessary as the Service objects are used in two different contexts:
      // - as a bank service, in bankServices.services; and
      // - as a plan service, in bankServices.plans.services.
      // If normalization is enabled, the cache will treat services object encountered in these
      // two contexts as the same and attempt to merge/replace/load the existing cached object.
      //
      // In the long term, the correct fix would be to split the Service object into BankService
      // and PlanService in the schema.
      //
      // ref: https://www.apollographql.com/docs/react/caching/cache-configuration/#disabling-normalization
      typePolicies: {
        Service: {
          keyFields: false,
        },
      },
      dataIdFromObject(o) {
        switch (o.__typename) {
          case "Company":
            if (o.uuid) return `CompanyBilling:${o.uuid}`;
            if (o.companyID) return `CompanyID:${o.companyID}`;
            return defaultDataIdFromObject(o);
          default:
            return defaultDataIdFromObject(o);
        }
      },
    }),
  });
};
